import { CurrencyPipe, LocationStrategy, PathLocationStrategy } from '@angular/common';
import { provideHttpClient, withFetch, withInterceptorsFromDi } from '@angular/common/http';
import { APP_ID, ApplicationConfig, importProvidersFrom, provideZoneChangeDetection } from '@angular/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import {
    provideRouter,
    UrlSerializer,
    withEnabledBlockingInitialNavigation,
    withInMemoryScrolling,
} from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { DynamicElementDefaultService, DynamicElementService, PAGE_SERVICE } from '@sirius/common';
import { provideWidgets } from 'widget';
import { CookieService } from 'ngx-cookie-service';
import { PageComponent } from './components/page/page.component';
import { DEFAULT_FALLBACK_LANG, I18N_FOLDER_PREFIX, I18N_SUFFIX } from './i18n.constants';
import { PageDefaultService } from './services';
import { storyblokPreviewCanActivate } from './services/storyblok-preview.guard';
import { TrailingSlashUrlSerializer } from './trailing-slash-url-serializer';

export const appConfig: ApplicationConfig = {
    providers: [
        // Angular 21 enables ZONELESS change detection by default in
        // `bootstrapApplication`. Our existing imperative patterns
        // (`PageService.pageChange.subscribe(p => this.page = p)`) rely on
        // Zone.js to detect mutations and tick change detection, so we have
        // to opt back in. See docs/angular-21-nx-22-upgrade.md §6 for the
        // full story; the signals/OnPush migration is a deferred follow-up.
        provideZoneChangeDetection(),
        // `withEnabledBlockingInitialNavigation()` is required because
        // `<butterfly-page>` is rendered directly from `AppComponent`'s
        // template (no `<router-outlet>`). Without blocking initial
        // navigation, the initial `NavigationEnd` is emitted *before*
        // `PageComponent.ngOnInit` subscribes to `Router.events`, so the
        // subscription misses it and `getPage` is never called for the
        // initial URL. Blocking nav defers `NavigationEnd` until after
        // bootstrap finishes, which `PageComponent.onNavigation` then
        // catches to issue the first `getPage(event.urlAfterRedirects)`.
        provideRouter(
            [
                {
                    path: '**',
                    component: PageComponent,
                    pathMatch: 'full',
                    canActivate: [storyblokPreviewCanActivate],
                },
            ],
            // Only emits `Scroll` events; `PageComponent` scrolls once the page has rendered.
            withInMemoryScrolling(),
            withEnabledBlockingInitialNavigation()
        ),
        // `provideClientHydration()` is intentionally NOT registered. The
        // workspace contains legacy DOM-mutating code (HeadService, Snowplow /
        // Cookiebot / Optimizely / Leadoo trackers, inline third-party scripts)
        // that would trip Angular's hydration mismatch detection. Without
        // hydration, the browser bootstrap destructively re-renders over the
        // SSR DOM — the historic SpaServices behavior. TransferState is
        // unaffected: `provideServerRendering()` registers the server-side
        // serializer and `@angular/core`'s `TransferState` factory auto-reads
        // the `<script id="serverApp-state">` tag on the client. Enabling
        // hydration is a deferred follow-up (docs/angular-21-nx-22-upgrade.md §13).
        provideAnimations(),
        provideHttpClient(withInterceptorsFromDi(), withFetch()),
        importProvidersFrom(
            TranslateModule.forRoot({
                fallbackLang: DEFAULT_FALLBACK_LANG,
                loader: provideTranslateHttpLoader({
                    prefix: I18N_FOLDER_PREFIX,
                    suffix: I18N_SUFFIX,
                    enforceLoading: false,
                    useHttpBackend: true,
                }),
                isolate: true,
            })
        ),
        { provide: APP_ID, useValue: 'serverApp' },
        // `useExisting` (not `useClass`) so the `PAGE_SERVICE` token resolves
        // to the same `providedIn: 'root'` singleton instance as a direct
        // `inject(PageDefaultService)`. `useClass` would create a separate
        // instance, with its own `pageChange` Subject — silently leading to
        // missed emissions if any consumer ever injected the concrete class.
        { provide: PAGE_SERVICE, useExisting: PageDefaultService },
        { provide: UrlSerializer, useClass: TrailingSlashUrlSerializer },
        { provide: LocationStrategy, useClass: PathLocationStrategy },
        { provide: DynamicElementService, useClass: DynamicElementDefaultService },
        provideWidgets(),
        CookieService,
        CurrencyPipe,
        // The SSR-only `ErrorHandler` that surfaces uncaught render errors onto
        // the response (as a `<script type="application/json" data-ssr-error>`
        // in `<head>`) is registered from `app.config.server.ts`. Keeping it
        // out of this shared config ensures the browser bundle never mutates
        // the live DOM on uncaught errors, which would otherwise pollute
        // real-user pages and break hydration once `provideClientHydration()`
        // is enabled. See docs/angular-21-nx-22-upgrade.md §12.
    ],
};
